import { ShippingMethod, Cart } from "@commercetools/platform-sdk"
import {
  Column,
  H6,
  Label2,
  RadioCard,
  Row,
  Select,
  simpleFadeTransitionProperties,
  TextVariants,
  UserInputStack
} from "@sixty-six-north/ui-system"
import { m } from "framer-motion"
import Script from "next/script"
import { useEffect, useState, FC, ReactNode, Fragment } from "react"
import { useTranslation } from "react-i18next"
import { Box } from "theme-ui"
import { useCartState } from "../../cart/CartServiceHook"
import { useConfig } from "../../Config"
import { ErrorCard } from "../../error/ErrorCard"
import { useTypedErrorApi } from "../../error/ErrorHooks"
import { ErrorType } from "../../error/ErrorType"
import { useStoreContext } from "../../i18n/StoreHooks"
import { fetchIcelandPostboxes, Postbox } from "../../shipping/IcelandPostbox"
import { scrollToFirstErrorCard } from "../../utils/Browser"
import logger from "../../utils/logger"
import { useErrorMessaging } from "../validation/ErrorMessaging"
import { useShippingMethodsService } from "./ShippingMethodService"

export enum ShippingMethodKeys {
  droppCollection = "dropp-collection",
  droppDelivery = "dropp-home-delivery",
  droppSamskip = "dropp-samskip",
  expressShipping = "express-shipping",
  standardShipping = "standard-shipping",
  nextDayShipping = "next-day-shipping",
  icelandPostbox = "iceland-post-po-box",
  icelandPost = "icelandic-postal-service",
  pickupAtMidhraun = "pickup-midhraun"
}

export const shippingMethodToTaxText =
  (t: (key: string) => string) => (shippingMethodKey: string) => {
    const methods: { [key in ShippingMethodKeys]?: string } = {
      [ShippingMethodKeys.expressShipping]: t("expressShippingTax"),
      [ShippingMethodKeys.standardShipping]: t("standardShippingTax"),
      [ShippingMethodKeys.nextDayShipping]: t("nextDayShippingTax")
    }
    return methods[shippingMethodKey]
  }

export const shippingMethodToDurationText =
  (t: (key: string) => string) =>
  (shippingMethodKey: string = ShippingMethodKeys.standardShipping) => {
    const methods: { [key in ShippingMethodKeys]?: string } = {
      [ShippingMethodKeys.expressShipping]: t("expressShippingDuration"),
      [ShippingMethodKeys.standardShipping]: t("standardShippingDuration"),
      [ShippingMethodKeys.nextDayShipping]: t("nextDayShippingDuration"),
      [ShippingMethodKeys.pickupAtMidhraun]: t("pickupAtMidhraunDuration")
    }

    return methods[shippingMethodKey]
  }

interface ShippingMethodsProps {
  cart?: Cart
  droppLocation?: DroppLocation
  shippingMethodChanges: ShippingMethodChanges
}

function invalidShippingMethods(shippingMethod: ShippingMethod): boolean {
  return shippingMethod.key !== undefined
}

interface DroppConfiguration {
  environment?: string
  storeId: string
}

const droppProdConfiguration: DroppConfiguration = {
  environment: undefined,
  storeId: "b9cecbca-767b-49b2-8d93-8e8f73403855"
}

const droppDevConfiguration: DroppConfiguration = {
  environment: "stage",
  storeId: "03e6a32c-7299-4757-804d-491c49c8cd07"
}

const Dropp: FC<{
  droppLocation?: DroppLocation
}> = ({ droppLocation }) => {
  const config = useConfig()

  const droppConfiguration =
    config.environment === "prod"
      ? droppProdConfiguration
      : droppDevConfiguration
  return (
    <Fragment>
      <Script
        type="application/javascript"
        src="https://app.dropp.is/dropp-locations.min.js"
        data-store-id={droppConfiguration.storeId}
        data-env={droppConfiguration.environment}
        async
      />
      {droppLocation && (
        <Fragment>
          <Box sx={{ color: "grey.1", mb: 8 }}>
            <Label2>{droppLocation.name}</Label2>
          </Box>
          <Box sx={{ color: "grey.1", mb: 8 }}>
            <Label2>{droppLocation.address}</Label2>
          </Box>
        </Fragment>
      )}
    </Fragment>
  )
}

const IcelandPostBox: React.FC<{
  method: ShippingMethod
  selectShippingMethod: (method?: ShippingMethod) => void
  onPostboxChange: (p?: Postbox) => void
}> = ({ method, selectShippingMethod, onPostboxChange }) => {
  const { t } = useTranslation("checkout")
  const cart = useCartState()

  const { isShoppingInIceland } = useStoreContext()

  const [postboxes, setPostboxes] = useState<Postbox[]>([])

  useEffect(() => {
    const selectedPostbox = postboxes?.find(
      it => it.postboxId === cart?.shippingAddress?.externalId
    )
    if (selectedPostbox) {
      onPostboxChange(selectedPostbox)
    }
  }, [postboxes])

  useEffect(() => {
    if (isShoppingInIceland()) {
      fetchIcelandPostboxes().then(result =>
        setPostboxes(result.postboxes || [])
      )
    }
  }, [])

  return postboxes.length > 0 ? (
    <Box sx={{ mt: 8 }}>
      <Select
        id="postbox-selector"
        name="postbox"
        label={t("chooseAPostbox")}
        onChange={event => {
          const selectedPostboxes = postboxes.filter(
            it => it.postboxId === event.target.value
          )

          if (selectedPostboxes.length === 0) {
            selectShippingMethod(undefined)
            onPostboxChange(undefined)
          } else {
            selectShippingMethod(method)
            onPostboxChange(selectedPostboxes[0])
          }
        }}
      >
        <option value="none">-</option>
        {postboxes.map((postbox, index) => {
          return (
            <option
              value={postbox.postboxId}
              key={`postbox-${index}`}
              selected={cart?.shippingAddress?.externalId === postbox.postboxId}
            >
              {postbox.name}
            </option>
          )
        })}
      </Select>
    </Box>
  ) : null
}

const doNothing = () => {
  logger.info()
}
const ShippingMethodRadio: FC<{
  method: ShippingMethod
  selectedShippingMethod?: string
  selectShippingMethod: (method?: ShippingMethod) => void
  onClick: () => void
  children?: ReactNode
}> = ({
  method,
  selectedShippingMethod,
  selectShippingMethod,
  children,
  onClick
}) => {
  const { t } = useTranslation("checkout")
  const cart = useCartState()
  const { formatTypedMoney } = useStoreContext()

  const getShippingMethodTax = shippingMethodToTaxText(t)
  const getShippingMethodDuration = shippingMethodToDurationText(t)

  const shippingMethodService = useShippingMethodsService()

  const { shippingRate, isFreeShipping, isIcelandPostbox } =
    shippingMethodService.getShippingRateInfoForCart(cart, method)

  const shippingMethodTax = getShippingMethodTax(method.key!)
  const shippingMethodDuration = getShippingMethodDuration(method.key!)
  return shippingRate ? (
    <RadioCard
      name="shippingMethod"
      value={method.key}
      checked={selectedShippingMethod === method.key}
      key={method.key}
      onClick={onClick}
      onChange={event => {
        if (!isIcelandPostbox) {
          selectShippingMethod(method)
        }
      }}
    >
      <Column sx={{ width: "100%" }}>
        <Row sx={{ width: "100%" }}>
          <Box sx={{ mb: 4, color: "black.1" }}>
            <Label2>{method.name}</Label2>
          </Box>
          <Box sx={{ marginLeft: "auto" }}>
            <Label2>
              {isFreeShipping
                ? formatTypedMoney({
                    ...shippingRate.price,
                    centAmount: 0
                  })
                : formatTypedMoney(shippingRate.price)}
            </Label2>
          </Box>
        </Row>

        {shippingMethodDuration && (
          <Box
            as="ul"
            sx={{
              listStyle: "none",
              color: "grey.1",
              mb: 8,
              variant: `text.${TextVariants.technicalParagraphSmall}`
            }}
          >
            {shippingMethodDuration
              .split("\n")
              .map((line: string, idx: number) => (
                <li key={idx}>{line}</li>
              ))}
          </Box>
        )}

        {children}

        {shippingMethodTax && (
          <Box sx={{ color: "grey.1", mb: 8 }}>
            <Label2>{shippingMethodTax}</Label2>
          </Box>
        )}
      </Column>
    </RadioCard>
  ) : null
}

interface ShippingMethodChanges {
  onPostboxChange: (postbox?: Postbox) => void
  onDroppLocationChange: (droppLocation?: DroppLocation) => void
  onShippingMethodChange: (method?: ShippingMethod) => void
}

export const ShippingMethods = ({
  cart,
  shippingMethodChanges,
  droppLocation
}: ShippingMethodsProps) => {
  const { t } = useTranslation("checkout")

  const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([])
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<
    ShippingMethod | undefined
  >(undefined)

  const { setErrorMessages } = useErrorMessaging()
  const shippingMethodService = useShippingMethodsService()
  const shippingError = useTypedErrorApi(ErrorType.ShippingMethodError)

  useEffect(() => {
    const noShippingMethodsError = {
      [ErrorType.ShippingMethodError]: [
        { message: t("noShippingMethodsAvailable") }
      ]
    }
    const fetchShippingMethodsForLocation = () => {
      if (!cart?.shippingAddress) {
        return []
      }
      return shippingMethodService
        .fetchShippingMethods({
          cartId: cart.id
        })
        .then(it => {
          setShippingMethods(it)
          switch (it.length) {
            case 0:
              return setErrorMessages(noShippingMethodsError)
            case 1:
              selectShippingMethod(it[0])
              break
            default:
              it.forEach(method => {
                if (method.id === cart?.shippingInfo?.shippingMethod?.id) {
                  selectShippingMethod(method)
                }
              })
          }
        })
        .catch(() => setErrorMessages(noShippingMethodsError))
    }

    fetchShippingMethodsForLocation()
  }, [cart?.shippingAddress])

  useEffect(() => {
    if (shippingError.error) {
      scrollToFirstErrorCard({ delay: 250 })
    }
  }, [shippingError.error])

  const selectShippingMethod = (method: ShippingMethod | undefined) => {
    shippingMethodChanges.onPostboxChange(undefined)
    setSelectedShippingMethod(method)
    shippingMethodChanges.onShippingMethodChange(method)
  }

  const openDroppPopup = () => {
    window
      ?.chooseDroppLocation()
      .then(shippingMethodChanges.onDroppLocationChange)
      .catch(e => logger.error("Error", e))
  }

  return (
    <>
      <Box sx={{ mt: 32, marginBottom: 0, color: "black.1" }}>
        <H6 id="shipping-method-label">
          {t("shippingMethod", { defaultValue: "Shipping method" })}
        </H6>
      </Box>
      <Box sx={{ marginBottom: 16, color: "grey.0" }}>
        <Label2>
          {shippingMethods.length === 0
            ? t("enterAddressToChooseShippingMethods")
            : shippingMethods.length === 1
              ? t("onlyStandardShipping")
              : t("chooseYourShippingMethod")}
        </Label2>
      </Box>
      <ErrorCard
        error={ErrorType.ShippingMethodError}
        clearAfterDisplay={true}
      />
      <Box role="radiogroup" aria-labelledby="shipping-method-label">
        <UserInputStack>
          {shippingMethods && shippingMethods.length > 0 && (
            <m.div
              initial={{ opacity: 0, height: "4rem" }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ ...simpleFadeTransitionProperties }}
            >
              {shippingMethods.filter(invalidShippingMethods).map(method => {
                const children =
                  method.key === ShippingMethodKeys.icelandPostbox ? (
                    <IcelandPostBox
                      selectShippingMethod={selectShippingMethod}
                      method={method}
                      onPostboxChange={shippingMethodChanges.onPostboxChange}
                    />
                  ) : method.key === ShippingMethodKeys.droppCollection ? (
                    <Dropp droppLocation={droppLocation} />
                  ) : null
                return (
                  <ShippingMethodRadio
                    key={method.key}
                    method={method}
                    selectShippingMethod={selectShippingMethod}
                    selectedShippingMethod={selectedShippingMethod?.key}
                    onClick={
                      method.key === ShippingMethodKeys.droppCollection
                        ? openDroppPopup
                        : doNothing
                    }
                  >
                    {children}
                  </ShippingMethodRadio>
                )
              })}
            </m.div>
          )}
        </UserInputStack>
      </Box>
    </>
  )
}
